import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuoteForm = ({ t, lang }) => {
  const [form, setForm] = useState({ name: "", location: "", phone: "", project_type: "interior_furnishing", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { ...form, lang });
      toast.success(t.form.successTitle, { description: t.form.successMsg });
      setForm({ name: "", location: "", phone: "", project_type: "interior_furnishing", message: "" });
    } catch (err) {
      toast.error(t.form.errorTitle, { description: t.form.errorMsg });
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "bg-white border-[#3E2723]/20 focus:border-[#3E2723] rounded-sm h-12 text-[#2D1810] placeholder:text-[#3E2723]/40";

  return (
    <section id="quote" data-testid="quote-section" className="pt-24 lg:pt-32 pb-14 lg:pb-16 bg-[#F5F2EC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#A67C00] mb-4">
            {t.form.overline}
          </p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl text-[#2D1810] tracking-tight">{t.form.title}</h2>
          <p className="mt-6 text-base sm:text-lg text-[#3E2723]/70 leading-relaxed">{t.form.subtitle}</p>
          <div className="mt-10 overflow-hidden rounded-sm hidden lg:block">
            <img
              src="/images/carving-detail.webp"
              alt={t.gallery.items["carving-detail"]}
              data-testid="quote-side-image"
              className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-500"
            />
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          onSubmit={submit}
          data-testid="quote-form"
          className="lg:col-span-7 bg-white border border-[#3E2723]/15 rounded-sm p-8 lg:p-12"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#5D4037] mb-2">
                {t.form.name}
              </label>
              <Input
                data-testid="quote-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder={t.form.namePh}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#5D4037] mb-2">
                {t.form.location}
              </label>
              <Input
                data-testid="quote-location-input"
                required
                value={form.location}
                onChange={set("location")}
                placeholder={t.form.locationPh}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#5D4037] mb-2">
                {t.form.phone}
              </label>
              <Input
                data-testid="quote-phone-input"
                required
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                placeholder={t.form.phonePh}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#5D4037] mb-2">
                {t.form.type}
              </label>
              <Select value={form.project_type} onValueChange={set("project_type")}>
                <SelectTrigger data-testid="quote-type-select" className={inputCls}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {Object.entries(t.form.types).map(([k, v]) => (
                    <SelectItem key={k} value={k} data-testid={`quote-type-${k}`}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-6">
            <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#5D4037] mb-2">
              {t.form.message}
            </label>
            <Textarea
              data-testid="quote-message-input"
              required
              rows={5}
              value={form.message}
              onChange={set("message")}
              placeholder={t.form.messagePh}
              className="bg-white border-[#3E2723]/20 focus:border-[#3E2723] rounded-sm text-[#2D1810] placeholder:text-[#3E2723]/40"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="quote-submit-button"
            className="mt-8 w-full sm:w-auto inline-flex justify-center items-center bg-[#D4AF37] text-[#2D1810] px-10 py-4 text-sm font-semibold rounded-sm hover:bg-[#c9a227] hover:-translate-y-0.5 transition-[transform,background-color] duration-300 disabled:opacity-60"
          >
            {sending ? t.form.sending : t.form.submit}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
